import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, AlertTriangle, Clock, ArrowRight, CheckCircle2, XCircle, FileText, CalendarCheck } from "lucide-react";
import { SiteLayout } from "@/components/site/SiteLayout";
import { PageSEO } from "@/components/site/PageSEO";
import { CTASection } from "@/components/site/CTASection";
import { ResourceHero } from "@/components/site/ResourceHero";
import { QuickAnswer } from "@/components/site/QuickAnswer";
import { getNoticeById, irsNotices } from "@/data/irsNotices";
import { noticeContent } from "@/data/irsNoticeContent";
import { brand, founder } from "@/data/site/brand";
import { irsForms } from "@/data/site/irsForms";


const urgencyBadge: Record<string, string> = {
  low: "bg-blue-500/15 text-blue-200 border-blue-400/30",
  medium: "bg-amber-500/15 text-amber-200 border-amber-400/30",
  high: "bg-orange-500/20 text-orange-200 border-orange-400/40",
  critical: "bg-red-500/20 text-red-200 border-red-400/40",
};

const urgencyLabel: Record<string, string> = {
  low: "Low urgency",
  medium: "Act soon",
  high: "Urgent",
  critical: "Critical — act now",
};

const IRSNoticeDetail = () => {
  const { noticeId = "" } = useParams();
  const notice = getNoticeById(noticeId);
  if (!notice) return <Navigate to="/resources/irs-notices" replace />;

  const content = noticeContent[notice.id];
  const related = irsNotices.filter((n) => n.id !== notice.id).slice(0, 4);

  const facts = [
    { label: "Notice", value: notice.number },
    { label: "Deadline", value: notice.deadline ?? "—" },
    { label: "Sent by", value: "IRS" },
  ];

  const formLookup = new Map(irsForms.map((f) => [f.number, f]));
  const linkedForms = content?.relatedForms
    ?.map((n) => formLookup.get(n))
    .filter((f): f is NonNullable<typeof f> => Boolean(f)) ?? [];

  const canonicalUrl = `${brand.siteUrl}/resources/irs-notices/${notice.id}`;
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `IRS Notice ${notice.number}: ${notice.name}`,
    description: notice.description,
    datePublished: "2025-01-01",
    dateModified: content?.lastReviewed ?? "2025-11-01",
    author: {
      "@type": "Person",
      name: founder.name,
      jobTitle: founder.title,
    },
    publisher: {
      "@type": "Organization",
      name: brand.legalName,
      url: brand.siteUrl,
    },
    mainEntityOfPage: canonicalUrl,
    image: notice.image ? `${brand.siteUrl}${notice.image}` : undefined,
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: [".speakable"],
    },
  };

  const faqSchema = content?.faqs?.length
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: content.faqs.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      }
    : null;

  const howToSchema = content?.stepsToRespond?.length
    ? {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: `How to respond to IRS Notice ${notice.number}`,
        description: `Step-by-step response to an IRS ${notice.number} (${notice.name}).`,
        totalTime: notice.deadline ? `P${notice.deadlineDays ?? 30}D` : undefined,
        step: content.stepsToRespond.map((s, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: s.title,
          text: s.description,
        })),
      }
    : null;

  const jsonLd = [articleSchema, faqSchema, howToSchema].filter(Boolean) as object[];



  return (
    <SiteLayout>
      <PageSEO
        title={`IRS Notice ${notice.number}: ${notice.name} — Deadline & How to Respond`}
        description={(content?.quickAnswer ?? notice.description).slice(0, 155)}
        path={`/resources/irs-notices/${notice.id}`}
        image={notice.image}
        ogType="article"
        author={founder.name}
        modifiedTime={content?.lastReviewed}
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "Resources", href: "/resources" },
          { name: "IRS Notices", href: "/resources/irs-notices" },
          { name: notice.number, href: `/resources/irs-notices/${notice.id}` },
        ]}
        jsonLd={jsonLd}
      />

      <ResourceHero
        breadcrumbs={[
          { name: "Home", href: "/" },
          { name: "IRS Notices", href: "/resources/irs-notices" },
          { name: notice.number },
        ]}
        eyebrow={`IRS Notice ${notice.number}`}
        title={notice.name}
        meta={
          <>
            <span
              className={`text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1 rounded-full border ${urgencyBadge[notice.urgency]}`}
            >
              {urgencyLabel[notice.urgency]}
            </span>
            {notice.deadline && (
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/80 px-2.5 py-1 rounded-full bg-white/5 border border-white/10">
                <Clock className="w-3.5 h-3.5" /> Respond within {notice.deadline}
              </span>
            )}
          </>
        }
        summary={notice.description}
        facts={facts}
        background="noticeEnvelope"
      />


      <section className="py-12 lg:py-16 bg-white">
        <div className="container mx-auto px-4 grid lg:grid-cols-[1fr_280px] gap-12 lg:gap-16 max-w-5xl">
          <article className="min-w-0 space-y-12">
            {/* TL;DR / Quick answer for AI Overviews */}
            {content && (
              <QuickAnswer
                eyebrow="Quick answer"
                heading={`What is IRS Notice ${notice.number}?`}
                answer={content.quickAnswer}
                bullets={content.quickAnswerBullets}
              />
            )}

            {/* On-page table of contents */}
            {content && (
              <nav aria-label="On this page" className="not-prose -mt-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">
                  On this page
                </p>
                <ul className="flex flex-wrap gap-x-4 gap-y-1.5 text-sm">
                  <li><a href="#why" className="text-navy hover:text-gold underline-offset-2 hover:underline">Why you got it</a></li>
                  <li><a href="#consequences" className="text-navy hover:text-gold underline-offset-2 hover:underline">If you ignore it</a></li>
                  <li><a href="#how-to-respond" className="text-navy hover:text-gold underline-offset-2 hover:underline">How to respond</a></li>
                  <li><a href="#mistakes" className="text-navy hover:text-gold underline-offset-2 hover:underline">Common mistakes</a></li>
                  <li><a href="#faqs" className="text-navy hover:text-gold underline-offset-2 hover:underline">FAQs</a></li>
                </ul>
              </nav>
            )}

            {notice.image && (
              <figure className="border border-border rounded-lg overflow-hidden bg-muted/30">
                <img
                  src={notice.image}
                  alt={`Sample first page of IRS Notice ${notice.number} — ${notice.name}`}
                  loading="lazy"
                  className="w-full h-auto"
                 width={816} height={1056} />
                <figcaption className="px-5 py-3 text-xs text-muted-foreground flex items-center justify-between gap-3 border-t border-border bg-white">
                  <span>Sample first page of an IRS {notice.number} notice.</span>
                  {notice.pdfUrl && (
                    <a
                      href={notice.pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-semibold text-navy hover:text-gold inline-flex items-center gap-1 whitespace-nowrap"
                    >
                      View on IRS.gov <ArrowRight className="w-3.5 h-3.5" />
                    </a>
                  )}
                </figcaption>
              </figure>
            )}

            {/* Why you got it */}
            {content?.whyYouGotIt?.length ? (
              <section id="why" className="scroll-mt-24">
                <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy mb-4 leading-tight">
                  Why you got an IRS {notice.number}
                </h2>
                <ul className="space-y-2.5">
                  {content.whyYouGotIt.map((r, i) => (
                    <li key={i} className="flex gap-3 text-[17px] text-foreground/85 leading-[1.7]">
                      <span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-gold flex-shrink-0" />
                      <span>{r}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/* Consequences */}
            <section id="consequences" className="scroll-mt-24 border-l-4 border-red-500 bg-red-50/40 rounded-r-lg p-6">
              <h2 className="font-heading text-xl lg:text-2xl font-bold text-red-900 mb-3 leading-tight flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" /> What happens if you ignore this notice
              </h2>
              <p className="text-foreground/85 leading-[1.75] text-[17px]">{notice.consequences}</p>
            </section>

            {/* How to respond — numbered steps */}
            {content?.stepsToRespond?.length ? (
              <section id="how-to-respond" className="scroll-mt-24">
                <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy mb-2 leading-tight">
                  How to respond to a {notice.number}
                </h2>
                {notice.deadline && (
                  <p className="text-sm text-muted-foreground mb-6 flex items-center gap-1.5">
                    <CalendarCheck className="w-4 h-4 text-gold" />
                    Recommended completion window: within {notice.deadline} of the notice date
                  </p>
                )}
                <ol className="space-y-5 counter-reset">
                  {content.stepsToRespond.map((step, i) => (
                    <li key={i} className="grid grid-cols-[40px_1fr] gap-4">
                      <div className="w-10 h-10 rounded-full bg-navy text-white font-heading font-bold text-base flex items-center justify-center">
                        {i + 1}
                      </div>
                      <div className="pt-1">
                        <h3 className="font-heading font-bold text-navy text-lg leading-snug mb-1.5">
                          {step.title}
                        </h3>
                        <p className="text-foreground/80 leading-[1.7] text-[16px]">{step.description}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>
            ) : (
              <section>
                <h2 className="font-heading text-2xl font-bold text-navy mb-3">What to do next</h2>
                <p className="text-foreground/85 leading-[1.75] text-[17px]">{notice.whatToDo}</p>
              </section>
            )}

            {/* Common mistakes */}
            {content?.commonMistakes?.length ? (
              <section id="mistakes" className="scroll-mt-24">
                <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy mb-4 leading-tight">
                  Common mistakes with a {notice.number}
                </h2>
                <ul className="space-y-3">
                  {content.commonMistakes.map((m, i) => (
                    <li key={i} className="flex gap-3 text-[17px] text-foreground/85 leading-[1.7]">
                      <XCircle className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            {/* Related forms */}
            {linkedForms.length > 0 && (
              <section className="scroll-mt-24 border-t border-border pt-10">
                <h2 className="font-heading text-xl font-bold text-navy mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-gold" /> IRS forms referenced
                </h2>
                <ul className="grid sm:grid-cols-2 gap-3">
                  {linkedForms.map((f) => (
                    <li key={f.id} className="border border-border rounded-md p-4 bg-secondary/30">
                      <p className="font-heading font-bold text-navy text-sm">{f.number}</p>
                      <p className="text-sm text-foreground/75 mt-0.5">{f.title}</p>
                      <Link
                        to="/resources/irs-forms"
                        className="text-xs font-semibold text-navy hover:text-gold inline-flex items-center gap-1 mt-2"
                      >
                        View form details <ArrowRight className="w-3 h-3" />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* FAQs (FAQPage schema) */}
            {content?.faqs?.length ? (
              <section id="faqs" className="scroll-mt-24">
                <h2 className="font-heading text-2xl lg:text-3xl font-bold text-navy mb-5 leading-tight">
                  Frequently asked questions about {notice.number}
                </h2>
                <div className="divide-y divide-border border-y border-border">
                  {content.faqs.map((f, i) => (
                    <details key={i} className="group py-5 [&_summary::-webkit-details-marker]:hidden">
                      <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                        <h3 className="font-heading font-bold text-navy text-base lg:text-lg leading-snug">
                          {f.question}
                        </h3>
                        <span className="text-gold font-bold text-xl leading-none mt-0.5 group-open:rotate-45 transition-transform">
                          +
                        </span>
                      </summary>
                      <p className="mt-3 text-foreground/80 leading-[1.75] text-[16px]">{f.answer}</p>
                    </details>
                  ))}
                </div>
              </section>
            ) : null}

            {/* Author / E-E-A-T */}
            <section className="border-t border-border pt-8 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gold/15 flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-gold" />
              </div>
              <div className="text-sm text-foreground/80 leading-relaxed">
                <p>
                  Reviewed by <Link to="/about/franklin-sofi" className="font-semibold text-navy hover:text-gold underline-offset-2 hover:underline">{founder.name}</Link>, {founder.credentials} — {founder.title} at {brand.shortName}.{" "}
                  {content?.lastReviewed && (
                    <>Last reviewed {new Date(content.lastReviewed).toLocaleDateString("en-US", { month: "long", year: "numeric" })}.</>
                  )}
                </p>
                <p className="mt-1.5 text-muted-foreground text-xs">
                  This page is general information, not legal or tax advice. For specific guidance on your IRS notice, contact a qualified CPA or tax resolution specialist.
                </p>
              </div>
            </section>
          </article>

          <aside className="space-y-10 lg:sticky lg:top-24 lg:self-start">
            <div className="bg-navy text-white rounded-lg p-5">
              <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-gold mb-2">
                Got this notice?
              </p>
              <p className="text-sm text-white/85 mb-4 leading-relaxed">
                A CPA can respond on your behalf — and in most cases, change the outcome. Free consultation.
              </p>
              <a
                href={`tel:${brand.phoneTel}`}
                className="inline-flex w-full items-center justify-center gap-2 bg-gold hover:bg-gold-dark text-navy font-bold px-5 py-3 rounded text-sm"
              >
                <Phone className="w-4 h-4" /> {brand.phone}
              </a>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-4">
                Other notices
              </p>
              <ul className="space-y-3">
                {related.map((r) => (
                  <li key={r.id}>
                    <Link
                      to={`/resources/irs-notices/${r.id}`}
                      className="text-sm text-foreground/80 hover:text-gold block"
                    >
                      <span className="font-bold text-navy">{r.number}</span> — {r.name}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link
                to="/resources/irs-notices"
                className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-navy hover:text-gold"
              >
                All notices <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>

            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-gold mb-3">
                Related tools
              </p>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/resources/notice-lookup" className="text-navy hover:text-gold font-semibold">
                    Notice lookup tool →
                  </Link>
                </li>
                <li>
                  <Link to="/resources/irs-forms" className="text-navy hover:text-gold font-semibold">
                    IRS form library →
                  </Link>
                </li>
                <li>
                  <Link to="/resources/glossary" className="text-navy hover:text-gold font-semibold">
                    Tax resolution glossary →
                  </Link>
                </li>
              </ul>
            </div>
          </aside>
        </div>
      </section>


      <CTASection />
    </SiteLayout>
  );
};

export default IRSNoticeDetail;
